import React, { useState } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AppProvider, useApp } from './contexts/AppContext';
import { HamburgerMenu } from './components/Layout/HamburgerMenu';
import { BottomNav } from './components/Layout/BottomNav';
import { Home } from './pages/Home';
import { Checklists } from './pages/Checklists';
import { Reports } from './pages/Reports';
import { IPCOfficer } from './pages/IPCOfficer';
import { IPCDashboard } from './pages/IPCDashboard';
import { Profile } from './pages/Profile';
import { Settings } from './pages/Settings';
import { SwitchWard } from './pages/SwitchWard';
import { ChecklistDetail } from './pages/ChecklistDetail';
import { ChecklistSummary } from './pages/ChecklistSummary';
import { StaffBuzzer } from './pages/StaffBuzzer';
import { Login } from './pages/Login';
import { Notifications } from './pages/Notifications';
import { MenuIcon, BellIcon, SunIcon, MoonIcon } from 'lucide-react';
import { wardNames } from './data/checklists';
function AppContent() {
  const {
    colors,
    theme,
    setTheme
  } = useTheme();
  const {
    isOnline,
    user,
    currentWard,
    isAuthenticated
  } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);
  const [checklistSummaryData, setChecklistSummaryData] = useState<{
    checklistId: string;
    completedSteps: string[];
    duration: number;
  } | null>(null);
  const [bottomNavTab, setBottomNavTab] = useState<'home' | 'checklists' | 'reports'>('home');
  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    if (page === 'home') setBottomNavTab('home');else if (page === 'checklists') setBottomNavTab('checklists');else if (page === 'reports') setBottomNavTab('reports');
  };
  const handleBottomNavChange = (tab: 'home' | 'checklists' | 'reports') => {
    setBottomNavTab(tab);
    setCurrentPage(tab);
  };
  const handleChecklistSelect = (checklistId: string) => {
    setSelectedChecklistId(checklistId);
    setCurrentPage('checklist-detail');
  };
  const handleChecklistSubmit = (data: {
    checklistId: string;
    completedSteps: string[];
    duration: number;
  }) => {
    setChecklistSummaryData(data);
    setCurrentPage('checklist-summary');
  };
  const handleBackToChecklists = () => {
    setSelectedChecklistId(null);
    setChecklistSummaryData(null);
    setCurrentPage('checklists');
  };
  const handleLogin = () => {
    window.location.reload();
  };
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} data-id="element-0" />;
  }
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home onNavigate={handleNavigate} data-id="element-1" />;
      case 'checklists':
        return <Checklists onChecklistSelect={handleChecklistSelect} data-id="element-2" />;
      case 'reports':
        return <Reports onBack={() => handleNavigate('home')} data-id="element-3" />;
      case 'ipc-officer':
        return <IPCOfficer onNavigate={handleNavigate} data-id="element-4" />;
      case 'ipc-dashboard':
        return <IPCDashboard data-id="element-5" />;
      case 'profile':
        return <Profile onBack={() => handleNavigate('home')} data-id="element-6" />;
      case 'settings':
        return <Settings onBack={() => handleNavigate('home')} data-id="element-7" />;
      case 'switch-ward':
        return <SwitchWard onComplete={() => handleNavigate('home')} data-id="element-8" />;
      case 'notifications':
        return <Notifications onBack={() => handleNavigate('home')} data-id="element-9" />;
      case 'staff-buzzer':
        return <StaffBuzzer onBack={() => handleNavigate('home')} data-id="element-10" />;
      case 'checklist-detail':
        return selectedChecklistId ? <ChecklistDetail checklistId={selectedChecklistId} onBack={handleBackToChecklists} onSubmit={handleChecklistSubmit} data-id="element-11" /> : null;
      case 'checklist-summary':
        return checklistSummaryData ? <ChecklistSummary checklistId={checklistSummaryData.checklistId} completedSteps={checklistSummaryData.completedSteps} duration={checklistSummaryData.duration} onBack={handleBackToChecklists} data-id="element-12" /> : null;
      default:
        return <Home onNavigate={handleNavigate} data-id="element-13" />;
    }
  };
  const showHeader = !['profile', 'notifications', 'settings', 'reports', 'staff-buzzer', 'checklist-summary'].includes(currentPage);
  return <div className="w-full min-h-screen pb-16" style={{
    backgroundColor: colors.background,
    color: colors.textPrimary
  }} data-id="element-14">
      {!isOnline && <div className="fixed top-0 left-0 right-0 z-30 px-4 py-2 text-center text-sm" style={{
      backgroundColor: colors.warning,
      color: '#000'
    }} data-id="element-15">
          No internet — records will sync automatically when online
        </div>}
      {showHeader && <header className="sticky top-0 z-20 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider,
      marginTop: !isOnline ? '32px' : '0'
    }} data-id="element-16">
          <div className="flex items-center justify-between p-4" data-id="element-17">
            <button onClick={() => setMenuOpen(true)} className="p-2 rounded-lg transition-colors" style={{
          color: colors.textPrimary
        }} aria-label="Open menu" data-id="element-18">
              <MenuIcon size={24} data-id="element-19" />
            </button>
            {currentPage === 'home' ? <div className="flex-1 mx-4" data-id="element-20">
                <h1 className="text-lg font-bold" data-id="element-21">
                  Welcome, {user?.shortname}
                </h1>
                <p className="text-sm" style={{
            color: colors.textSecondary
          }} data-id="element-22">
                  {wardNames[currentWard]}
                </p>
              </div> : <h1 className="text-lg font-bold" data-id="element-23">CDSS</h1>}
            <div className="flex items-center gap-2" data-id="element-24">
              <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} className="p-2 rounded-lg transition-colors" style={{
            color: colors.textPrimary
          }} aria-label="Toggle theme" data-id="element-25">
                {theme === 'light' ? <MoonIcon size={20} data-id="element-26" /> : <SunIcon size={20} data-id="element-27" />}
              </button>
              <button onClick={() => handleNavigate('notifications')} className="p-2 rounded-lg transition-colors relative" style={{
            color: colors.textPrimary
          }} aria-label="Notifications" data-id="element-28">
                <BellIcon size={20} data-id="element-29" />
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{
              backgroundColor: colors.error
            }} data-id="element-30" />
              </button>
            </div>
          </div>
        </header>}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} onNavigate={handleNavigate} data-id="element-31" />
      <main className={showHeader ? 'p-4' : ''} data-id="element-32">{renderPage()}</main>
      <BottomNav activeTab={bottomNavTab} onTabChange={handleBottomNavChange} data-id="element-33" />
    </div>;
}
export function App() {
  return <ThemeProvider data-id="element-34">
      <AppProvider data-id="element-35">
        <AppContent data-id="element-36" />
      </AppProvider>
    </ThemeProvider>;
}